// ============================================================================
// OnboardingKanban — 8-stage board over the UNION of employee + partner
// onboarding records. Mirrors the new-account-sop kanban (dnd-kit, optimistic
// drag-to-restage). Drag is enabled only when onStageChange is provided
// (hr / superuser); otherwise cards are tap-to-open only.
//
// 2026-10-08 (item 818): each person arranges THEIR view of the board through
// the shared board view control — sort, pin, drag into their order. Saved per
// person in the database; changes no record.
// ============================================================================
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrangeCards,
  BoardCard,
  BoardViewControls,
  boardCollision,
  resolveBoardDrop,
  useBoardViewPrefs,
  type BoardViewApi,
} from '../../shared/board-view';
import { ONBOARDING_STAGES, stageLabel, type OnboardingRecord, type OnboardingStage } from './types';
import OnboardingCard from './onboarding-card';

interface Props {
  records: OnboardingRecord[];
  onSelect: (rec: OnboardingRecord) => void;
  onStageChange?: (rec: OnboardingRecord, next: OnboardingStage) => Promise<void> | void;
}

function keyOf(rec: OnboardingRecord) {
  return `${rec.source}:${rec.onboarding_id}`;
}

const recordFacts = (r: OnboardingRecord) => ({
  id: keyOf(r),
  createdAt: r.created_at ?? null,
  startDate: r.start_date ?? null,
});

export default function OnboardingKanban({ records, onSelect, onStageChange }: Props) {
  const [activeDrag, setActiveDrag] = useState<OnboardingRecord | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const view = useBoardViewPrefs('onboarding');

  // Column headers are shared by every card on the board. They read the sub-track
  // words only when every card shown is a sub or partner card (item 871); a board
  // that mixes in W2 cards keeps the W2 words, since one header cannot say both.
  const allSubTrack = records.length > 0 && records.every((r) => r.type !== 'W2');
  const columns = useMemo(
    () =>
      ONBOARDING_STAGES.map((stage) => ({
        stage,
        label: stageLabel(stage, allSubTrack ? 'Subcontractor' : 'W2'),
        rows: arrangeCards(
          records.filter((r) => !r.terminal && r.stage === stage),
          recordFacts,
          view.prefs,
        ),
      })),
    [records, view.prefs, allSubTrack],
  );

  function handleDragStart(e: DragStartEvent) {
    const found = records.find((r) => keyOf(r) === String(e.active.id));
    setActiveDrag(found ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveDrag(null);
    if (!onStageChange || !e.over) return;
    const rec = records.find((r) => keyOf(r) === String(e.active.id));
    if (!rec) return;
    const drop = resolveBoardDrop(e.over.id, e.over.data.current as { columnId?: string } | undefined, rec.stage);
    if (drop.kind === 'reorder') {
      const shown = columns.find((c) => c.stage === rec.stage)?.rows.map(keyOf) ?? [];
      view.place(shown, keyOf(rec), drop.overCardId);
      return;
    }
    if (drop.kind !== 'move') return;
    const next = drop.columnId as OnboardingStage;
    if (!(ONBOARDING_STAGES as readonly string[]).includes(next)) return;
    if (rec.stage === next) return;
    try {
      await onStageChange(rec, next);
    } catch (err) {
      console.error('[onboarding-kanban] stage change failed', err);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <BoardViewControls view={view} hasStartDate className="mb-3" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        {columns.map((c) => (
          <KanbanColumn
            key={c.stage}
            stage={c.stage}
            label={c.label}
            rows={c.rows}
            onSelect={onSelect}
            draggable={Boolean(onStageChange)}
            view={view}
          />
        ))}
      </div>
      <DragOverlay>
        {activeDrag ? (
          <div className="w-64 rotate-1">
            <OnboardingCard rec={activeDrag} onClick={() => {}} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function KanbanColumn({
  stage,
  label,
  rows,
  onSelect,
  draggable,
  view,
}: {
  stage: OnboardingStage;
  label: string;
  rows: OnboardingRecord[];
  onSelect: (rec: OnboardingRecord) => void;
  draggable: boolean;
  view: BoardViewApi;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const shownIds = rows.map(keyOf);
  return (
    <section
      ref={setNodeRef}
      className={`min-w-0 rounded-lg border p-2 transition ${
        isOver ? 'border-tcs-text bg-tcs-bg-muted' : 'border-transparent'
      }`}
    >
      <h2 className="mb-2 flex items-center justify-between px-1 text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted">
        <span className="truncate" title={label}>
          {label}
        </span>
        <span className="ml-1 shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">
          {rows.length}
        </span>
      </h2>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-3 text-center text-[11px] text-tcs-text-muted">
          Empty.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <BoardCard
              key={keyOf(r)}
              id={keyOf(r)}
              columnId={stage}
              shownIds={shownIds}
              view={view}
              draggable={draggable}
              as="li"
            >
              <OnboardingCard rec={r} onClick={onSelect} />
            </BoardCard>
          ))}
        </ul>
      )}
    </section>
  );
}
